import React, {useEffect, useState} from "react";
import {makeStyles} from "@material-ui/core/styles";
import AppBar from "@material-ui/core/AppBar";
import {ErrorScreen, HeaderToolbar, Loading} from "../../components";
import Toolbar from "@material-ui/core/Toolbar";
import {useAuthUser} from "../../components/auth-provider";
import {useStore} from "../../store";
import {invalidateAccount} from "../../store/actions";
import {Link} from "react-router-dom";
import Grid from "@material-ui/core/Grid";
import ausPhone from "../../utils/aus-phone";
import Button from "@material-ui/core/Button";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import FuseAnimate from "../../components/fuse-animate/fuse-animate";
import Card from "@material-ui/core/Card";
import Typography from "@material-ui/core/Typography";
import {AccountService} from "../../services";

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
    },
    appBar: {
        zIndex: theme.zIndex.drawer + 1,
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        marginTop: 16,
        justifyContent: "center",
    },
    card: {
        display: 'flex',
        flexDirection: 'column',
        padding: 24,
        width: '100%',
        maxWidth: 340,
        [theme.breakpoints.up('sm')]: {
            maxWidth: 360,
        },
    },
    listItem: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        marginBottom: theme.spacing(2),
        borderRadius: 3,
        maxWidth: 400,
    },
    number: {
        fontSize: 18,
        fontWeight: 700,
        '&.inActive': {
            textDecoration: 'line-through',
        },
    },
}));

//
// If the account has only 1 service, the recharge page redirects
// to the service recharge page, /recharge/<serviceId>.
//
// If the account has no services, it displays a prompt with a link
// to add a service /add-service
//
// If there is more than one service, a pick list is displayed to
// allow the user to choose which service to recharge.
//
const ReactivatePage = ({location}) => {
    const classes = useStyles();
    const {user} = useAuthUser();           // Get logged in user
    const [, dispatch] = useStore()
    const [state, setState] = useState({
        priors: [],
        isFetching: false,
        error: null,
    })

    // Make a reactivation query to get prior numbers for this account.
    useEffect(()=> {
        setState(state => ({...state, isFetching: true}));
        AccountService.reactivationQuery(user.accountId)
            .then(priors => {
                setState(state => ({...state, priors}));

                // PhoneThru accounts can be reactivated immediately
                AccountService.reactivate(user.accountId)
                    .then(result => {
                        if (result.state.code === 200) {
                            // success - nothing more to do
                            setState(state => ({...state, isFetching:false}));
                            // Invalidate the account so it's reloaded next time we need it.
                            dispatch(invalidateAccount());
                        } else {
                            // We hit a problem - display an error
                            const error = {
                                status: result.state.code,
                                statusText: `Request id ${result.id} could not be completed`,
                            }
                            // noinspection JSCheckFunctionSignatures
                            setState(state => ({...state, error, isFetching:false}));
                        }
                    })
                    .catch(error => {
                        setState(state => ({...state, error, isFetching:false}));
                    })
            })
            .catch(error => {
                setState(state => ({...state, error, isFetching:false}));
            })
    }, [user.accountId, dispatch])

    return (
        <div id="recharge" className={classes.root}>
            <AppBar position="fixed" className={classes.appBar}>
                <HeaderToolbar location={location.pathname}/>
            </AppBar>
            <Toolbar/>

            <main className={classes.content}>
                { state.isFetching ? <Loading/> : null }
                { state.error && <ErrorScreen error={state.error}/> }
                { state.error === null && !state.isFetching ?
                    <FuseAnimate animation="transition.expandIn" delay={100}>
                        <div id="reactivate" className="flex flex-grow flex-col items-center">
                            <ReactivatePanel state={state} setState={setState} />
                        </div>
                    </FuseAnimate>
                    : null
                }
            </main>
        </div>
    );

}

const ReactivatePanel = ({state}) => {
    const classes = useStyles();

    // Filter the priors for those that are available
    const recoverable = state.priors.filter(n => {return n.isAvailable});

    // Test stuff...
    // const recoverable = [
    //     {number: '+61398765432', zone:'Melbourne', isAvailable: true},
    //     {number: '+61354621789', zone:'Castlemaine', isAvailable: true}
    // ]
    // const recoverable = [];

    if (recoverable.length === 0) {
        // There's zero numbers...
        return (
            <Card className={classes.card}>
                <Typography variant="h5" color="secondary" className="pb-6">Reactivation Complete</Typography>
                <Typography variant="body1">Welcome back!</Typography>
                <Typography variant="body1" paragraph>Your account has been reactivated.</Typography>

                <div className="flex flex-grow flex-row mt-6">
                    <Button
                        component={Link}
                        to={'/add-service'}
                        variant="outlined"
                        className="w-full"
                        color="secondary"
                    >
                        Add a Number
                    </Button>
                </div>
            </Card>
        )

    } else {
        // There's > 1 number returned...
        return (
            <Card className={classes.card}>
                <Typography variant="h5" color="secondary" className="pb-6">Reactivation Complete</Typography>
                <Typography variant="body1" paragraph>Welcome back!</Typography>
                <Typography variant="body1" paragraph>Your account has been reactivated.</Typography>

                { recoverable.length === 1 ?
                    <Typography variant="body1" paragraph>We found a number you had before that's still available.</Typography>
                    :
                    <Typography variant="body1" paragraph>We found some numbers you had before that are still available.</Typography>
                }

                <List >
                    { recoverable.map((number) => (
                        <ListItem
                            key={number.number}
                            className={classes.listItem}
                        >
                            <Grid container direction="column" >
                                <div className={classes.number}>
                                    {ausPhone(number.number)}
                                </div>
                                { number.zone !=='' ? <div className="mb-1">{number.zone}</div>:null}
                            </Grid>
                        </ListItem>
                    ))}
                </List>

                { recoverable.length === 1 ?
                    <Typography variant="body1" paragraph>If you'd like to get this number back, tap the
                        button below then tap RECOVER A PREVIOUS NUMBER on the next page.
                    </Typography>
                    :
                    <Typography variant="body1" paragraph>If you'd like to get one of these numbers back, tap the
                        button below then tap RECOVER A PREVIOUS NUMBER on the next page.
                    </Typography>
                }

                <div className="flex flex-grow flex-row mt-6">
                    <Button
                        component={Link}
                        to={'/add-service'}
                        variant="outlined"
                        className="w-full"
                        color="secondary"
                    >
                        Add a Number
                    </Button>
                </div>
            </Card>
        )
    }
}

export default ReactivatePage;
